<template>
  <div ref="elementObserverRef" class="ds-lazy-image" :class="[expandClass]">
    <DsSkeleton
      v-if="(!loaded || populating) && !imgError"
      :mask="mask"
      :class="['ds-lazy-image-skeleton', populatingClass]"
    />
    <div
      v-else-if="(loaded || !populating) && imgError"
      :class="['ds-lazy-image-error', `ds-lazy-image-${mask}`]"
      data-testid="ds-lazy-image-error"
    >
      <slot name="errorSlot">
        <span class="ds-lazy-image-error__span">
          <DsImageSolidIcon class="ds-lazy-image-error__icon" />
        </span>
        {{ t("ds.component.image.dsLazyImage.error") }}
      </slot>
    </div>
    <img
      v-else
      :src="imgSrc"
      :class="[
        'ds-lazy-image-img',
        objectFitClass,
        loadedClass,
        `ds-lazy-image-${mask}`,
      ]"
      :alt="alt"
      draggable="false"
      data-testid="ds-lazy-image-loaded"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onMounted,
  type PropType,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

import DsImageSolidIcon from "../icon/media/DsImageSolidIcon.vue";
import DsSkeleton from "../skeleton/DsSkeleton.vue";

import type { DsMaskType } from "../../types";

defineOptions({
  name: "DsLazyImage",
});

const emit = defineEmits(["loaded", "error"]);

const props = defineProps({
  alt: {
    type: String,
    default: "",
  },
  src: {
    type: String,
    required: false,
  },
  populating: {
    type: Boolean,
    default: false,
  },
  populatingClass: {
    type: String,
    default: "",
  },
  cover: {
    type: Boolean,
    default: true,
  },
  mask: {
    type: String as PropType<DsMaskType>,
    default: "square",
  },
});

const { t } = useI18n();

const image = ref({} as HTMLImageElement);
const imgSrc = ref("");
const imgError = ref(false);
const loaded = ref(false);
const instance = ref();
const elementObserverRef = ref();

const objectFitClass = computed(() => {
  return {
    "object-cover": props.cover,
    "object-contain": !props.cover,
  };
});
const loadedClass = computed(() => {
  return {
    "ds-lazy-image-loaded": loaded.value && !props.populating,
  };
});
const expandClass = computed(() => {
  return {
    "ds-lazy-image-expand": props.populating || imgError.value,
  };
});

onMounted(() => {
  instance.value = getCurrentInstance();
  populatingData();
});

async function populatingData() {
  loaded.value = false;
  if (!window["IntersectionObserver"]) {
    await loadImage();
  } else {
    createObserver();
  }
}

function createObserver() {
  const observer = new IntersectionObserver(
    (intersectionEntry) => {
      intersectionEntry.forEach(({ isIntersecting }) => {
        if (isIntersecting && !loaded.value) {
          loadImage();
        }
      });
    },
    {
      root: null,
      threshold: 0,
    }
  );
  observer.observe(elementObserverRef.value);
}

function error(
  message = "The name of the image is missing",
  event?: string | Event
) {
  imgError.value = true;
  emit("error");
  const componentName = instance.value.$parent?.$parent?.$options.name;
  if (event instanceof Event) {
    throw new Error(
      `${message} ....error route: ${componentName} (${event.type})`
    );
  }
  throw new Error(`${message} ....error route: ${componentName}`);
}

async function loadImage() {
  //Only load the image if the src is defined.
  if (props.src !== undefined && props.src !== "") {
    imgError.value = false;
    image.value = new Image();
    image.value.onload = () => {
      loaded.value = true;
    };
    image.value.onerror = (event) => {
      error(`The image ${props.src} could not be loaded`, event);
    };
    image.value.src = props.src;

    imgSrc.value = image.value.src;
    emit("loaded");
  }
}

watch(
  () => props.src,
  (newSrc, oldSrc) => {
    if (oldSrc === undefined && newSrc === "" && !props.populating) {
      error(`The image path is invalid`);
    }
    if (oldSrc !== newSrc) {
      loaded.value = false;
      loadImage();
    }
  }
);

defineExpose({
  imgSrc,
  imgError,
  loaded,
  image,
});
</script>

<style lang="scss" scoped>
.ds-lazy-image {
  @apply max-w-3xl flex items-center justify-center;

  &-expand {
    @apply w-full h-full;
  }

  &-circle {
    @apply rounded-full;
  }

  &-square {
    @apply rounded-md;
  }

  &-skeleton {
    @apply h-full max-h-full w-full;
  }

  &-error {
    @apply h-full max-h-full w-full flex flex-col items-center justify-center bg-gray-200;

    &__span {
      @apply h-10 w-10 text-gray-400 mx-auto block;
    }

    &__icon {
      @apply h-10 w-10 fill-gray-400 mx-auto block;
    }
  }

  &-img {
    @apply transition-all ease-in delay-300 duration-300 aspect-auto h-full max-h-full w-full;
  }
}
</style>
